// Enhanced Auto Add Gift Product for Shopify - Immediate UI Update
// Add this to your theme's main JavaScript file or create a new snippet

class AutoGiftManager {
  constructor() {
    // Configure your products here
    this.config = {
      // Map qualifying product IDs to their gift variant IDs
      giftRules: {
        '9028926341432': '49994804592952', // Bambooless Sticks → Free Lotus Holder
        '9023476007224': '49994804592952', // Devgandha Pack → Free Lotus Holder
      },
      
      // Minimum quantity needed to trigger gift
      minimumQuantity: 1,
      
      // Gift product properties (to identify it as a gift)
      giftProperties: {
        '_gift_item': 'true',
        '_auto_added': 'true'
      }
    };
    
    this.isProcessing = false;
    this.lastCartState = null;
    this.lastUpdateTime = 0;
    this.updateCooldown = 1000; // 1 second cooldown between updates
    this.hasUpdatedThisSession = new Set(); // Track what we've already updated
    this.init();
  }
  
  init() {
    // Listen for cart updates
    document.addEventListener('DOMContentLoaded', () => {
      this.checkAndManageGifts();
    });
    
    // Listen for AJAX cart updates (if your theme uses AJAX)
    document.addEventListener('cart:updated', () => {
      this.checkAndManageGifts();
    });
    
    // Listen for add to cart events
    this.attachCartListeners();
  }
  
  attachCartListeners() {
    // For standard add to cart forms
    const addToCartForms = document.querySelectorAll('form[action*="/cart/add"]');
    addToCartForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        // Immediate check after form submission
        setTimeout(() => this.checkAndManageGifts(), 50);
        setTimeout(() => this.checkAndManageGifts(), 200);
        setTimeout(() => this.checkAndManageGifts(), 500);
      });
    });
    
    // For AJAX add to cart buttons
    const ajaxSelectors = [
      '[data-add-to-cart]', 
      '.btn-add-to-cart', 
      '[data-product-form]',
      '.product-form__cart-submit',
      '.btn.product-form__cart-submit',
      '.add-to-cart-button',
      'button[name="add"]',
      'input[name="add"]'
    ];
    
    ajaxSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(button => {
        button.addEventListener('click', () => {
          setTimeout(() => this.checkAndManageGifts(), 50);
          setTimeout(() => this.checkAndManageGifts(), 200);
          setTimeout(() => this.checkAndManageGifts(), 500);
        });
      });
    });
    
    // Listen for quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="quantity"], .cart-quantity, [data-quantity-input]')) {
        setTimeout(() => this.checkAndManageGifts(), 100);
      }
    });
    
    // Enhanced fetch interceptor
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const result = originalFetch.apply(this, args);
      
      // Check if it's a cart-related API call
      if (args[0] && (args[0].includes('/cart/add') || args[0].includes('/cart/update'))) {
        result.then(() => {
          // Multiple immediate checks with shorter intervals
          setTimeout(() => window.autoGiftManager?.checkAndManageGifts(), 10);
          setTimeout(() => window.autoGiftManager?.checkAndManageGifts(), 50);
          setTimeout(() => window.autoGiftManager?.checkAndManageGifts(), 150);
        });
      }
      
      return result;
    };
    
    // Listen for common theme cart events
    const cartEvents = ['cart:updated', 'cart:refresh', 'cart:build', 'cart:change', 'ajaxCart:updated'];
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, () => {
        setTimeout(() => this.checkAndManageGifts(), 50);
      });
    });
    
    // Enhanced DOM observer for cart changes
    if (window.MutationObserver) {
      const cartObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && 
              (mutation.target.closest('.cart, [data-cart], .cart-items, .cart-drawer') ||
               mutation.target.matches('.cart, [data-cart], .cart-items, .cart-drawer'))) {
            setTimeout(() => this.checkAndManageGifts(), 100);
          }
        });
      });
      
      // Observe cart containers and entire body for dynamic content
      const observeTargets = [
        ...document.querySelectorAll('.cart, [data-cart], .cart-items, .cart-drawer'),
        document.body
      ];
      
      observeTargets.forEach(target => {
        if (target) {
          cartObserver.observe(target, { 
            childList: true, 
            subtree: true,
            attributes: false
          });
        }
      });
    }
  }
  
  async checkAndManageGifts() {
    // Prevent multiple simultaneous executions
    if (this.isProcessing) return;
    
    // Cooldown check - but allow immediate execution for first time
    const now = Date.now();
    if (this.lastUpdateTime > 0 && now - this.lastUpdateTime < this.updateCooldown) {
      console.log('Auto gift check skipped - too frequent');
      return;
    }
    
    this.isProcessing = true;
    this.lastUpdateTime = now;
    
    try {
      const cart = await this.getCart();
      
      // Create a cart state signature to detect actual changes
      const cartSignature = this.createCartSignature(cart);
      
      // Skip if cart hasn't actually changed (but allow first run)
      if (this.lastCartState && this.lastCartState === cartSignature) {
        console.log('Cart state unchanged, skipping gift check');
        return;
      }
      
      console.log('Cart state changed, checking gifts...');
      this.lastCartState = cartSignature;
      
      // DEBUG: Log cart information
      console.log('Current cart items:', cart.items);
      console.log('Product IDs in cart:', cart.items.map(item => item.product_id));
      console.log('Configured gift rules:', this.config.giftRules);
      
      const qualifyingItems = this.getQualifyingItems(cart.items);
      const existingGifts = this.getExistingGifts(cart.items);
      
      console.log('Qualifying items found:', qualifyingItems);
      console.log('Existing gifts:', existingGifts);
      
      let giftActionTaken = false;
      
      // Add missing gifts
      for (const [productId, giftVariantId] of Object.entries(this.config.giftRules)) {
        const qualifyingQty = this.getQualifyingQuantity(qualifyingItems, productId);
        const existingGiftQty = this.getGiftQuantity(existingGifts, giftVariantId);
        
        const actionKey = `add_${productId}_${giftVariantId}`;
        
        if (qualifyingQty >= this.config.minimumQuantity && existingGiftQty === 0) {
          // Check if we've already tried to add this gift recently
          if (!this.hasUpdatedThisSession.has(actionKey)) {
            await this.addGiftToCart(giftVariantId);
            this.hasUpdatedThisSession.add(actionKey);
            giftActionTaken = true;
            break; // Only add one gift at a time to prevent conflicts
          }
        } else if (qualifyingQty < this.config.minimumQuantity && existingGiftQty > 0) {
          const removeKey = `remove_${productId}_${giftVariantId}`;
          if (!this.hasUpdatedThisSession.has(removeKey)) {
            await this.removeGiftFromCart(cart.items, giftVariantId);
            this.hasUpdatedThisSession.add(removeKey);
            giftActionTaken = true;
            break; // Only remove one gift at a time
          }
        }
      }
      
      // Clear session tracking periodically to allow re-additions if needed
      if (!giftActionTaken && this.hasUpdatedThisSession.size > 0) {
        // Clear tracking after 30 seconds to allow fresh attempts
        setTimeout(() => {
          this.hasUpdatedThisSession.clear();
          console.log('Session tracking cleared');
        }, 30000);
      }
      
    } catch (error) {
      console.error('Auto gift error:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  createCartSignature(cart) {
    // Create a unique signature for the cart state
    const items = cart.items.map(item => ({
      id: item.variant_id,
      qty: item.quantity,
      productId: item.product_id,
      isGift: item.properties && item.properties._gift_item === 'true'
    }));
    
    return JSON.stringify({
      itemCount: cart.item_count,
      items: items.sort((a, b) => a.id - b.id) // Sort for consistent comparison
    });
  }
  
  async getCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }
  
  getQualifyingItems(cartItems) {
    return cartItems.filter(item => 
      Object.keys(this.config.giftRules).includes(item.product_id.toString())
    );
  }
  
  getExistingGifts(cartItems) {
    return cartItems.filter(item => 
      item.properties && item.properties._gift_item === 'true'
    );
  }
  
  getQualifyingQuantity(qualifyingItems, productId) {
    return qualifyingItems
      .filter(item => item.product_id.toString() === productId)
      .reduce((total, item) => total + item.quantity, 0);
  }
  
  getGiftQuantity(giftItems, variantId) {
    return giftItems
      .filter(item => item.variant_id.toString() === variantId)
      .reduce((total, item) => total + item.quantity, 0);
  }
  
  async addGiftToCart(variantId) {
    try {
      console.log('Adding gift to cart:', variantId);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1,
            properties: this.config.giftProperties
          }]
        })
      });
      
      if (response.ok) {
        console.log('Gift added successfully - forcing immediate UI refresh');
        
        // Set a longer cooldown after adding a gift to prevent immediate re-checks
        this.lastUpdateTime = Date.now();
        this.updateCooldown = 3000; // 3 seconds after gift addition
        
        // Reset cooldown after the extended period
        setTimeout(() => {
          this.updateCooldown = 1000;
          console.log('Cooldown reset to normal');
        }, 5000);
        
        // Force immediate refresh with aggressive update
        await this.forceImmediateUpdateOnce();
        
        // Show notification
        this.showGiftNotification();
        
        // Additional fallback - if nothing else worked, reload cart area after a delay
        setTimeout(async () => {
          if (!this.isUpdatingUI) {
            console.log('Fallback: Additional refresh attempt');
            await this.updateCartCount();
            this.triggerCartEvents();
          }
        }, 500);
        
      } else {
        console.error('Failed to add gift, response not ok');
      }
    } catch (error) {
      console.error('Error adding gift:', error);
    }
  }
  
  async forceImmediateUpdateOnce() {
    // Prevent multiple simultaneous UI updates
    if (this.isUpdatingUI) return;
    this.isUpdatingUI = true;
    
    try {
      console.log('Starting immediate UI update...');
      
      // Strategy 1: Immediate cart count update
      await this.updateCartCount();
      
      // Strategy 2: Get fresh cart and force DOM update
      const cart = await this.getCart();
      await this.manuallyUpdateCartDOM(cart);
      
      // Strategy 3: Force section reload for immediate visibility
      await this.forceCartSectionReload();
      
      // Strategy 4: Trigger essential cart events
      this.triggerCartEvents();
      
      // Strategy 5: Try theme-specific updates
      this.tryThemeSpecificUpdatesOnce();
      
      console.log('Immediate UI update completed');
      
    } finally {
      // Reset the UI update flag after a shorter delay
      setTimeout(() => {
        this.isUpdatingUI = false;
      }, 1000);
    }
  }
  
  async forceCartSectionReload() {
    // Force immediate section reload with multiple fallbacks
    const sectionsToTry = [
      'cart-items',
      'cart-drawer', 
      'cart-drawer-items',
      'mini-cart'
    ];
    
    for (const sectionId of sectionsToTry) {
      try {
        const response = await fetch(`/cart?section_id=${sectionId}`);
        if (response.ok) {
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Try multiple selectors for this section
          const selectors = [
            `#${sectionId}`,
            `[data-section-id="${sectionId}"]`,
            `.${sectionId}`,
            `[data-${sectionId}]`,
            '.cart-items',
            '[data-cart-items]',
            '.cart__items',
            '.cart-drawer__items'
          ];
          
          for (const selector of selectors) {
            const currentElement = document.querySelector(selector);
            const newElement = doc.querySelector(selector);
            
            if (currentElement && newElement) {
              currentElement.innerHTML = newElement.innerHTML;
              console.log(`Successfully updated: ${selector}`);
              return; // Success! Exit all loops
            }
          }
        }
      } catch (error) {
        console.log(`Section ${sectionId} reload failed:`, error);
      }
    }
    
    // Fallback: If we're on cart page, force a page section reload
    if (window.location.pathname.includes('/cart')) {
      try {
        const response = await fetch('/cart');
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const cartSelectors = ['.cart', '.cart-form', 'main', '.main-content'];
        
        for (const selector of cartSelectors) {
          const currentCart = document.querySelector(selector);
          const newCart = doc.querySelector(selector);
          
          if (currentCart && newCart) {
            currentCart.innerHTML = newCart.innerHTML;
            console.log(`Cart page updated: ${selector}`);
            return;
          }
        }
      } catch (error) {
        console.log('Cart page reload failed:', error);
      }
    }
  }
  
  triggerCartEvents() {
    // Trigger only essential events to avoid loops
    const events = [
      'cart:updated',
      'cart:refresh',
      'cart:change'
    ];
    
    events.forEach(eventName => {
      // Dispatch on both document and window
      document.dispatchEvent(new CustomEvent(eventName, { 
        detail: { 
          source: 'auto-gift-update',
          timestamp: Date.now()
        }
      }));
      
      // Some themes listen on window
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent(eventName, { 
          detail: { 
            source: 'auto-gift-update',
            timestamp: Date.now()
          }
        }));
      }
    });
  }
  

  
  tryThemeSpecificUpdatesOnce() {
    // Try only the most common update function to avoid conflicts
    const updateFunctions = [
      () => window.theme?.cart?.refresh?.(),
      () => window.updateCart?.(),
      () => window.refreshCart?.(),
      () => window.theme?.cartDrawer?.getCart?.()
    ];
    
    for (const updateFunc of updateFunctions) {
      try {
        updateFunc();
        console.log('Theme-specific update called');
        break; // Only call the first available function
      } catch (error) {
        // Silently continue to next function
      }
    }
  }
  
  async manuallyUpdateCartDOM(cart) {
    // Try to manually inject the gift item into the cart DOM
    const cartContainers = document.querySelectorAll(
      '.cart-items, [data-cart-items], .cart__items, .cart-drawer__items'
    );
    
    if (cartContainers.length === 0) return;
    
    // Find the gift item in the cart data
    const giftItem = cart.items.find(item => 
      item.properties && item.properties._gift_item === 'true'
    );
    
    if (!giftItem) return;
    
    // Create a gift item HTML element
    const giftItemHTML = this.createGiftItemHTML(giftItem);
    
    cartContainers.forEach(container => {
      // Check if gift already exists in DOM
      const existingGift = container.querySelector('[data-gift-item="true"]');
      if (!existingGift) {
        // Add gift item to cart container
        if (container.children.length > 0) {
          container.insertAdjacentHTML('beforeend', giftItemHTML);
        } else {
          container.innerHTML = giftItemHTML;
        }
      }
    });
  }
  
  createGiftItemHTML(giftItem) {
    // Create a basic gift item HTML - customize this based on your theme structure
    return `
      <div class="cart-item gift-item" data-gift-item="true" data-variant-id="${giftItem.variant_id}">
        <div class="cart-item__image">
          <img src="${giftItem.featured_image?.url || giftItem.image}" alt="${giftItem.product_title}" width="60" height="60">
        </div>
        <div class="cart-item__details">
          <h4 class="cart-item__title">${giftItem.product_title}</h4>
          <p class="cart-item__variant">${giftItem.variant_title || ''}</p>
          <div class="cart-item__gift-badge" style="background: #4CAF50; color: white; padding: 2px 8px; border-radius: 3px; font-size: 12px; display: inline-block;">
            🎁 Free Gift
          </div>
          <div class="cart-item__quantity">
            <span>Qty: ${giftItem.quantity}</span>
          </div>
          <div class="cart-item__price">
            <span style="text-decoration: line-through;">${Shopify.formatMoney(giftItem.original_price)}</span>
            <span style="color: #4CAF50; font-weight: bold;">FREE</span>
          </div>
        </div>
      </div>
    `;
  }
  

  
  async removeGiftFromCart(cartItems, variantId) {
    const giftItem = cartItems.find(item => 
      item.variant_id.toString() === variantId && 
      item.properties && 
      item.properties._gift_item === 'true'
    );
    
    if (giftItem) {
      try {
        console.log('Removing gift from cart:', variantId);
        
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates: {
              [giftItem.variant_id]: 0
            }
          })
        });
        
        console.log('Gift removed successfully');
        
        // Set cooldown after removal
        this.lastUpdateTime = Date.now();
        this.updateCooldown = 2000;
        
        setTimeout(() => {
          this.updateCooldown = 1000;
        }, 3000);
        
        await this.forceImmediateUpdateOnce();
      } catch (error) {
        console.error('Error removing gift:', error);
      }
    }
  }
  
  async updateCartCount() {
    try {
      const cart = await this.getCart();
      
      // Update cart count in common selectors
      const countSelectors = [
        '.cart-count',
        '[data-cart-count]',
        '.cart-item-count',
        '.header-cart-count',
        '#cart-count',
        '.cart-count-bubble',
        '.cart-link .count'
      ];
      
      countSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.textContent = cart.item_count;
          // Also update any data attributes
          element.setAttribute('data-cart-count', cart.item_count);
        });
      });
      
      // Update cart total
      const totalSelectors = [
        '.cart-total',
        '[data-cart-total]',
        '.cart-subtotal',
        '.cart__total'
      ];
      
      totalSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const formattedPrice = typeof Shopify !== 'undefined' && Shopify.formatMoney 
            ? Shopify.formatMoney(cart.total_price)
            : `$${(cart.total_price / 100).toFixed(2)}`;
          element.textContent = formattedPrice;
        });
      });
      
    } catch (error) {
      console.log('Cart count update error:', error);
    }
  }
  
  showGiftNotification() {
    // Create a more prominent notification
    const notification = document.createElement('div');
    notification.className = 'gift-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #4CAF50, #45a049);
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      z-index: 99999;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 20px rgba(76, 175, 80, 0.3);
      animation: slideInRight 0.3s ease-out;
      max-width: 300px;
    `;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 20px;">🎁</span>
        <div>
          <div style="font-weight: bold;">Free Gift Added!</div>
          <div style="font-size: 12px; opacity: 0.9;">Check your cart to see your free item</div>
        </div>
      </div>
    `;
    
    // Add animation keyframes if not already added
    if (!document.querySelector('#gift-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'gift-notification-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(notification);
    
    // Remove notification after 4 seconds with animation
    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease-in forwards';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 4000);
  }
}

// Initialize the auto gift manager
document.addEventListener('DOMContentLoaded', () => {
  window.autoGiftManager = new AutoGiftManager();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.autoGiftManager = new AutoGiftManager();
  });
} else {
  window.autoGiftManager = new AutoGiftManager();
}

// For themes that use AJAX cart systems, also expose globally
window.AutoGiftManager = AutoGiftManager;